import * as React from "react";
import { useState } from "react";
import Autosuggest, { ChangeEvent } from "react-autosuggest";
import { observer } from "mobx-react";

import {
  isTrigramIndexable,
  SearchFilterKeys,
  SearchFilterProps,
  TRIGRAM_NOT_INDEXABLE_MESSAGE,
} from "../../utils/search";

const SEQUENCE_SEARCH_REGEX = /^[ACTG]{5,}$/i;
const MAX_SUGGESTION_LEN = 600;

function isGeneSearchUnavailable(suggestion: SearchFilterProps): boolean {
  return suggestion.key === "gene" && !isTrigramIndexable(String(suggestion.val));
}

function renderSuggestion(suggestion: SearchFilterProps) {
  const value = String(suggestion.val);
  const truncated =
    value.length > MAX_SUGGESTION_LEN ? `${value.substring(0, MAX_SUGGESTION_LEN)}…` : value;

  if (isGeneSearchUnavailable(suggestion)) {
    return (
      <span className="search-suggestion--unavailable">
        {truncated} ({TRIGRAM_NOT_INDEXABLE_MESSAGE})
      </span>
    );
  }
  return <span>{truncated}</span>;
}

function renderSectionTitle(section: SearchFilterProps) {
  return <strong>{section.title}</strong>;
}

interface Props {
  performSearch: (key: SearchFilterKeys, value: string) => void;
  searchSections: SearchFilterProps[];
  placeholder?: string;
}

function SearchSuggestionBox({ performSearch, searchSections, placeholder }: Props) {
  const [value, setValue] = useState<string>("");
  const [suggestions, setSuggestions] = useState<SearchFilterProps[]>([]);

  const hasSequenceSearchSection = searchSections.some((section) => section.key === "sequence");

  const getSectionSuggestions = (section: SearchFilterProps | undefined) => {
    if (section !== undefined) {
      return [section];
    }
    return [];
  };

  const onChange = (event: React.FormEvent<HTMLElement>, _: ChangeEvent) => {
    setValue((event.currentTarget as HTMLInputElement).value);
  };

  const onSuggestionsFetchRequested = (value: string) => {
    const valueWithoutWhitespace = value.replace(/\s/g, "");
    const isSequenceSearch =
      hasSequenceSearchSection && SEQUENCE_SEARCH_REGEX.test(valueWithoutWhitespace);

    const suggestions = searchSections
      .filter((section) => {
        if (section.key === "sequence") {
          return isSequenceSearch;
        }
        return !isSequenceSearch;
      })
      .map((section) => {
        // Setting the string input as value for all sections
        section.val = isSequenceSearch ? valueWithoutWhitespace : value;
        return section;
      });
    setSuggestions(suggestions);
  };

  const onSuggestionsClearRequested = () => {
    setValue("");
    setSuggestions([]);
  };

  const onSuggestionSelected = (suggestion: SearchFilterProps, sectionIndex: number | null) => {
    if (sectionIndex === null) {
      return;
    }
    if (suggestion.disabled || isGeneSearchUnavailable(suggestion)) {
      return;
    }
    performSearch(suggestion.key, String(suggestion.val));
  };

  const inputProps = { placeholder: placeholder || "Type for Advanced Search…", value, onChange };

  return (
    <Autosuggest<SearchFilterProps>
      suggestions={suggestions.filter((s) => !s.disabled)}
      multiSection={true}
      highlightFirstSuggestion={true}
      onSuggestionsFetchRequested={({ value }) => onSuggestionsFetchRequested(value)}
      onSuggestionsClearRequested={onSuggestionsClearRequested}
      onSuggestionSelected={(_e, { suggestion, sectionIndex }) =>
        onSuggestionSelected(suggestion, sectionIndex)
      }
      getSuggestionValue={(v) => v.key}
      renderSuggestion={renderSuggestion}
      renderSectionTitle={renderSectionTitle}
      getSectionSuggestions={getSectionSuggestions}
      inputProps={inputProps}
    />
  );
}

export default observer(SearchSuggestionBox);
